// Lib
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

// Utils
import globalLogger from '../../../logger';
import getClientConfig from '../../getClientConfig';
import { awsWafIntegrationAvailable } from './awsTokenUtils';

// Interceptors
import requestAuthorisationInterceptor from './requestAuthorisationInterceptor';
import requestWafTokenInterceptor from './requestWafTokenInterceptor';
import responseRetryInterceptor from '../../../../common/utils/http/responseRetryInterceptor';
import requestNoCacheInterceptor from './requestNoCacheInterceptor';
import responseChallengeInterceptor from '../../../../common/utils/http/responseChallengeInterceptor';

// Constants
import { METHODS } from '../../../../common/utils/http/httpConstants';

const logger = globalLogger.createChannel('http');

const clientConfig = getClientConfig();
const apiRootUrl = clientConfig.apiRoot || '';

export const DEFAULT_CONFIG: AxiosRequestConfig = {
    method: METHODS.GET,
    timeout: 21000,
    responseType: 'json',
    prefix: `${apiRootUrl}/api`,
    headers: {
        'Accept-Version': clientConfig.appVersion || '1.0.0',
        'Content-Type': 'application/json',
    },
};

export type MilanoteAxiosRequestConfig<BodyType = any> = AxiosRequestConfig<BodyType> & {
    skipAuthHeader?: boolean;
};

// Request interceptors
axios.interceptors.request.use(requestAuthorisationInterceptor);
axios.interceptors.request.use(requestNoCacheInterceptor);
axios.interceptors.request.use(requestWafTokenInterceptor);

// Response interceptors
const retryErrorCb = (err: AxiosError) =>
    // Sensitive data audit: SAFE — warn/error auto-sanitized (error/response sanitized; no raw JSON stringified payload)
    logger.warn(
        `RETRYING: A http error occurred (${(err.config?.retry || 0) - (err.config?._retryCount || 0)} attempts remaining)`,
        err,
        { status: err.response?.status },
    );
const retry = responseRetryInterceptor(axios, retryErrorCb);
axios.interceptors.response.use(responseChallengeInterceptor(awsWafIntegrationAvailable, retry), retry);

export const http = async <BodyType = any, ResponseType = any>(
    config: MilanoteAxiosRequestConfig<BodyType>,
): Promise<AxiosResponse<ResponseType, BodyType>> => {
    const axiosConfig: MilanoteAxiosRequestConfig = {
        ...DEFAULT_CONFIG,
        ...config,
        headers: {
            ...DEFAULT_CONFIG.headers,
            ...config.headers,
        },
    };

    axiosConfig.url = `${axiosConfig.prefix}/${config.url}`;

    return axios(axiosConfig);
};
